import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button } from './ui/button';

interface PaginationFooterProps {
  currentPage: number;
  totalPages: number;
  totalResults: number;
  startResult: number;
  endResult?: number;
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onLast: () => void;
  isLoading?: boolean;
  className?: string;
}

const PaginationFooter: React.FC<PaginationFooterProps> = ({
  currentPage,
  totalPages,
  totalResults,
  startResult,
  endResult,
  onFirst,
  onPrev,
  onNext,
  onLast,
  isLoading = false,
  className = ''
}) => {
  if (isLoading || totalResults === 0) {
    return null;
  }

  const containerClass = className
    ? `flex items-center justify-between px-2 ${className}`.trim()
    : 'flex items-center justify-between px-2';

  return (
    <div className={containerClass}>
      <div className="hidden flex-1 text-muted-foreground text-sm md:block">
        Mostrando {startResult}{endResult && endResult !== startResult ? `-${endResult}` : ''} de {totalResults} resultados
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex w-[100px] items-center justify-center font-medium text-sm">
          Pagina {currentPage} de {totalPages}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="icon"
          onClick={onFirst}
          disabled={currentPage === 1}
          className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
        >
          <span className="sr-only">Ir a la primera pagina</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m11 17-5-5 5-5" />
            <path d="m18 17-5-5 5-5" />
          </svg>
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onPrev}
          disabled={currentPage === 1}
          className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
        >
          <span className="sr-only">Ir a la pagina anterior</span>
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onNext}
          disabled={currentPage === totalPages}
          className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
        >
          <span className="sr-only">Ir a la pagina siguiente</span>
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onLast}
          disabled={currentPage === totalPages}
          className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
        >
          <span className="sr-only">Ir a la ultima pagina</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m6 17 5-5-5-5" />
            <path d="m13 17 5-5-5-5" />
          </svg>
        </Button>
      </div>
    </div>
  );
};

export default PaginationFooter;
